// M13 — admin Users screen. Lists every account in the DB with its
// character (if any), last login, and current token_version. The single
// action exposed is "Invalidate sessions" which bumps token_version
// server-side AND closes any live WS bound to that account.

import { el } from "./dom.js";

export interface UserRow {
  id: string;
  email: string;
  createdAt: number;
  lastLoginAt: number;
  tokenVersion: number;
  characterName: string | null;
}

export interface UsersScreenCallbacks {
  onInvalidate: (accountId: string, email: string) => void;
  onRefresh: () => void;
}

export interface UsersScreen {
  root: HTMLElement;
  setRows(rows: UserRow[]): void;
  setError(reason: string | null): void;
}

function formatRelative(ms: number): string {
  if (ms <= 0) return "just now";
  const secs = Math.floor(ms / 1000);
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 60 / 24);
  return `${days}d ago`;
}

export function createUsersScreen(cb: UsersScreenCallbacks): UsersScreen {
  let rows: UserRow[] = [];
  let errorMsg: string | null = null;

  const body = el("div", { class: "editor users-screen" });
  const root = el("div", { class: "items-layout users-layout" }, body);

  function render(): void {
    body.replaceChildren();

    body.append(
      el("h2", {}, "Users"),
      el(
        "p",
        { class: "users-hint" },
        "Tous les comptes enregistrés. \"Invalidate sessions\" incrémente le token_version : tous les JWT existants deviennent invalides instantanément, et les sessions live sont kickées. L'utilisateur devra redemander un OTP pour se reconnecter.",
      ),
      el(
        "div",
        { class: "users-toolbar" },
        el(
          "button",
          { class: "button secondary", onclick: () => cb.onRefresh() },
          "Refresh",
        ),
        el("span", { class: "users-count" }, `${rows.length} account(s)`),
      ),
    );

    if (errorMsg) {
      body.append(el("div", { class: "users-error" }, `Erreur : ${errorMsg}`));
    }

    if (rows.length === 0) {
      body.append(el("p", { class: "users-empty" }, "Aucun compte enregistré."));
      return;
    }

    const now = Date.now();
    const table = el(
      "table",
      { class: "users-table" },
      el(
        "thead",
        {},
        el(
          "tr",
          {},
          el("th", {}, "Email"),
          el("th", {}, "Character"),
          el("th", {}, "Created"),
          el("th", {}, "Last login"),
          el("th", {}, "Token version"),
          el("th", {}, "Actions"),
        ),
      ),
    );

    const tbody = el("tbody", {});
    for (const r of rows) {
      tbody.append(
        el(
          "tr",
          {},
          el("td", { class: "users-email" }, r.email),
          el(
            "td",
            {},
            r.characterName
              ? el("span", { class: "users-character" }, r.characterName)
              : el("span", { class: "users-no-character" }, "—"),
          ),
          el("td", {}, formatRelative(now - r.createdAt)),
          el("td", {}, formatRelative(now - r.lastLoginAt)),
          el(
            "td",
            { class: "users-tv" },
            r.tokenVersion === 0
              ? el("span", { class: "users-tv-fresh" }, "0")
              : el("span", { class: "users-tv-bumped" }, String(r.tokenVersion)),
          ),
          el(
            "td",
            { class: "users-actions" },
            el(
              "button",
              {
                class: "button danger",
                onclick: () => {
                  if (
                    !confirm(
                      `Invalider toutes les sessions de ${r.email} ?\n\nL'utilisateur sera déconnecté et devra refaire un OTP pour se reconnecter.`,
                    )
                  )
                    return;
                  cb.onInvalidate(r.id, r.email);
                },
              },
              "Invalidate sessions",
            ),
          ),
        ),
      );
    }
    table.append(tbody);
    body.append(table);
  }

  render();

  return {
    root,
    setRows(next: UserRow[]): void {
      rows = next;
      errorMsg = null;
      render();
    },
    setError(reason: string | null): void {
      errorMsg = reason;
      render();
    },
  };
}
